import {
  AddBoxRounded,
  AddRounded,
  AdminPanelSettingsRounded,
  SendTwoTone,
  SettingsApplicationsRounded,
  TimelapseRounded,
} from "@mui/icons-material";
import { Grid } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import Dialogs from "src/components/Dialogs";
import FormBuilder from "src/components/formBuilder/FormBuilder";
import LottieAnimation from "src/components/LottieAnimation";
import PinAuthorizedView from "src/components/PinAuthorizedView";
import arkadu from "src/coreConfig";
import useAsyncRequest from "src/hooks/useAsyncRequest";
import { useCanDo } from "src/hooks/useCando";
import useShop from "src/hooks/useShop.ts";
import { useUserAccount } from "src/hooks/useUserAccount";
import animationData from "src/lotties/arkadu_linear_loading";
import { setShopState } from "src/slices/shop";
import { useDispatch, useSelector } from "src/store";
import BasePage from "src/views/BasePage";
import AccountCard from "./AccountCard";
import DialogAddUserProduct from "./dialogs/user_account/DialogAddUserProduct";

const AccountContext = React.createContext();

const viewStates = {
  addProduct: "addProduct",
  transferFunds: "transferFunds",
  showPendingDebits: "showPendingDebits",
  showAccountHistory: "showAccountHistory",
  adjustment: "adjustment",
};
const AccountLayout = ({ children }) => {
  const { id } = useParams();
  const { t } = useTranslation();

  const { currentCustomer } = useSelector((state) => state.customers);
  const { currentAccount, sectionTitle, reloadRequest, fundGroups } =
    useSelector((state) => state.shop);
  const { userAccount, accountError, isError, isLoading, reloadAccount } =
    useUserAccount(id, currentCustomer?.id);
  const { api } = useAsyncRequest();
  const { canDo } = useCanDo("shopping_cart");
  const { getFundGroups } = useShop();
  const { adminMode } = useSelector((state) => state.settings);
  const { enqueueSnackbar } = useSnackbar();
  const [accountViewStates, setAccountViewStates] = React.useState({
    state: null,
    message: "",
  });
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const handleAddProduct = async (values) => {
    setAccountViewStates({ state: null, message: "" });
    var res = api(
      arkadu.urls.v2.account.handle,
      {
        customer_id: currentCustomer.id,
        mode: adminMode ? "admin" : "user",
        instance: "user_product",
        action: "add",
        user_account_id: currentAccount.id,
        product_id: values.product_id.value,
        quantity: values.quantity,
        comment: values.comment,
      },
      {},
      t("Agregar producto"),
    );
    reloadAccount();
  };

  useEffect(() => {
    // dispatch(setShopState({ currentAccount: userAccount }));
    if (
      userAccount &&
      accountViewStates.state === viewStates.adjustment &&
      fundGroups.length === 0
    ) {
      getFundGroups();
    }
  }, [userAccount, viewStates.state, fundGroups.length]);

  useEffect(() => {
    if (reloadRequest) {
      reloadAccount();
    }
    dispatch(setShopState({ reloadRequest: false }));
  }, [reloadRequest]);

  if (!currentAccount) {
    return <LottieAnimation animationData={animationData} />;
  }

  if (location.pathname.includes("accounts/pay/")) {
    return (
      <AccountContext.Provider
        value={{ currentAccount, reloadAccount, isLoading }}
      >
        {children}
      </AccountContext.Provider>
    );
  }

  return (
    <AccountContext.Provider
      value={{ currentAccount, reloadAccount, isLoading }}
    >
      <BasePage
        title={sectionTitle || t("Detalle de cuenta")}
        actionsMenu={[
          {
            label: t("Agregar fondos"),
            disabled: currentAccount?.blocked,

            onClick: () => {
              history.push(
                `/${currentCustomer.shortname}/accounts/pay/${currentAccount.id}`,
              );
            },
            variant: "contained",
            color: "secondary",
            icon: <AddRounded />,
            keepOnMobile: true,
          },
          {
            label: t("Historial de pagos"),
            onClick: () => {
              history.push(
                `/${currentCustomer.shortname}/accounts/funds/${currentAccount.id}`,
              );
            },
            icon: <TimelapseRounded />,
            keepOnMobile: true,
          },
          canDo("can_view_useraccount") && {
            label: t("Admin"),
            icon: <AdminPanelSettingsRounded />,
            color: "success",
            items: [
              canDo("can_add_userproduct") && {
                label: t("Agregar Producto"),
                onClick: () =>
                  setAccountViewStates({
                    ...accountViewStates,
                    state: viewStates.addProduct,
                  }),
                icon: <AddBoxRounded color="info" />,
                color: "info",
              },
              canDo("can_add_accounting_adjustment") && {
                label: t("Realizar Ajuste"),
                color: "info",
                onClick: () => {
                  setAccountViewStates({
                    ...accountViewStates,
                    state: viewStates.adjustment,
                  });
                },
                icon: <SettingsApplicationsRounded color="info" />,
              },
              canDo("can_transfer_addfund") && {
                label: t("Transferir Fondos"),
                color: "error",
                onClick: () => {
                  history.push(
                    `/${currentCustomer.shortname}/accounts/${currentAccount.id}/transfer`,
                  );
                },
                icon: <SendTwoTone color="error" />,
              },
            ],
          },
        ]}
      >
        <Dialogs
          value={accountViewStates.state}
          onClose={() =>
            setAccountViewStates({ ...accountViewStates, state: null })
          }
          dialogs={[
            {
              value: viewStates.addProduct,
              title: t("Agregar Producto"),
              color: "secondary",
              body: (
                <DialogAddUserProduct
                  userAccount={currentAccount}
                  open={accountViewStates.state === viewStates.addProduct}
                  onClose={() =>
                    setAccountViewStates({ ...accountViewStates, state: null })
                  }
                />
              ),
            },
            {
              value: viewStates.adjustment,
              title: t("Ajuste contable"),
              body: (
                <PinAuthorizedView>
                  <FormBuilder
                    onSubmit={async (values) => {
                      var _res = await api(arkadu.urls.v2.account.handle, {
                        customer_id: currentCustomer.id,
                        mode: adminMode ? "admin" : "user",
                        instance: "account_adjustment",
                        action: "add",
                        account_id: currentAccount.id,
                        amount: values.amount,
                        justification: values.justification,
                        operation: values.operation.value,
                        bank_id: values.bank_id?.value,
                        not_fund: values.not_fund,
                        apply_at: values.apply_at,
                        related_to: values.related_to?.value,
                      });
                      if (_res.data.res === 1) {
                        enqueueSnackbar(t("Ajuste realizado"), {
                          variant: "success",
                        });
                        setAccountViewStates({
                          ...accountViewStates,
                          state: null,
                        });
                        reloadAccount();
                      }
                    }}
                    fieldSet={[
                      {
                        id: "amount",
                        label: t("Monto"),
                        type: "number",
                      },
                      {
                        id: "justification",
                        label: t("Justificación"),
                        type: "textarea",
                      },
                      {
                        id: "operation",
                        label: t("Operación"),
                        type: "select",
                        options: [
                          { value: "credit", label: t("Crédito") },
                          { value: "debit", label: t("Débito") },
                        ],
                        initialValue: { value: "credit", label: t("Crédito") },
                      },
                      {
                        id: "related_to",
                        label: t("Relacionado con"),
                        conditionalBy: (values) => {
                          return values.operation.value === "debit";
                        },
                        type: "select",
                        optional: true,
                        options: [
                          { value: "", label: t("Seleccione un grupo") },
                        ].concat(
                          fundGroups.map((fg) => ({
                            value: fg.related_to,
                            label: fg.name,
                          })),
                        ),
                        initialValue: {
                          value: "",
                          label: t("Seleccione un grupo"),
                        },
                      },
                      {
                        id: "not_fund",
                        label: t("Sin fondo en banco"),
                        type: "boolean",
                        conditionalBy: (values) => {
                          return values.operation.value === "credit";
                        },
                      },
                      {
                        id: "bank_id",
                        label: t("Banco"),
                        type: "select",
                        options: currentCustomer.accounts.map((i) => ({
                          value: i.id,
                          label: i.name,
                        })),
                        optional: true,
                        conditionalBy: (values) => {
                          return (
                            values.operation.value === "credit" &&
                            !values.not_fund
                          );
                        },
                      },
                      {
                        id: "apply_now",
                        label: t("Aplicar ahora"),
                        type: "boolean",
                        conditionalBy: (values) => {
                          return values.operation.value === "credit";
                        },
                      },
                      {
                        id: "apply_at",
                        label: t("Aplicar en"),
                        type: "date",
                        optional: true,
                        initialValue: new Date(),
                        conditionalBy: (values) => {
                          return (
                            values.operation.value === "credit" &&
                            !values.apply_now
                          );
                        },
                      },
                    ]}
                  />
                </PinAuthorizedView>
              ),
              actions: [],
            },
          ]}
        />
        {!window.location.pathname.includes("accounts/pay/") && (
          <Grid container sx={{ display: "flex", justifyContent: "center" }}>
            <AccountCard />
          </Grid>
        )}

        {children}
      </BasePage>
    </AccountContext.Provider>
  );
};

export default AccountLayout;

export { AccountContext };
